import type {PageProps} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Image from '@/components/image'
import RichText, {hasRichText} from '@/components/rich-text'
import {Table} from '@/components/table'
import {Text} from '@/components/text'
import type {PricingPageQuery} from '@/graphql-types'
import {useGlobalStrings} from '@/providers/global-strings'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithAccordion from '@/sections/title-with-accordion'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import {Section, BackgroundWrapper, Layout} from '@/styles/grid'
import {fonts, colors, tokens} from '@/theme'
import type {FormConfig} from '@/types/consents'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

export const dataTestId = 'pricing-page'

type PricingPageProps = {
    data: PageProps<PricingPageQuery>['data']['prismicPricingPage']['data']
    children: React.ReactNode
}

export const PricingPage = ({data, children}: PricingPageProps) => {
    const globalStrings = useGlobalStrings()

    const hasStarsRating = data.social_proof_ratings_section.document.data.ratings.some(
        (item) => !!item.rating.document.data.rating_number,
    )

    return (
        <>
            {hasRichText(data.title.richText) && (
                <TitleSection data-testid={`${dataTestId}-title-section`}>
                    <RichText
                        forwardedAs="h1"
                        content={data.title.richText}
                        $variant="heading-large"
                    />
                    {data.title_image && (
                        <StyledImage image={data.title_image} data-testid="pricing-hero-image" />
                    )}
                </TitleSection>
            )}

            {children}

            {!!data.tables.filter(({table}) => !!table).length && (
                <BackgroundWrapper $color="muted">
                    <TablesWrapper data-testid={`${dataTestId}-table`}>
                        <TitleWrapper $variant="two-asymmetrical">
                            <Text $variant="heading-base">{data.tables_title}</Text>
                        </TitleWrapper>

                        {data.tables.map(({table}) => {
                            if (!table.document) {
                                return null
                            }
                            const hasEssentialTier = table.document.data.essential_heading
                            const hasBeyondTier = !!table.document.data.beyond_heading

                            const {
                                category,
                                essential_heading,
                                premium_heading,
                                pro_heading,
                                beyond_heading,
                                rows,
                            } = table.document.data

                            const headColumns = [
                                category,
                                ...(hasEssentialTier ? [essential_heading] : []),
                                pro_heading,
                                premium_heading,
                                ...(hasBeyondTier ? [beyond_heading] : []),
                            ]
                            const bodyHeads = rows.map((el) => el.feature)

                            const bodyHeadsTooltips = rows.map((el) => el.feature_tooltip || '')

                            const bodyColumns = rows.map((el) => [
                                ...(hasEssentialTier ? [el.essential_text || el.essential] : []),
                                el.pro_text || el.pro,
                                el.premium_text || el.premium,
                                ...(hasBeyondTier ? [el.beyond_text || el.beyond] : []),
                            ])

                            return (
                                <StyledTable
                                    key={table.document.id}
                                    headColumns={headColumns}
                                    bodyHeads={bodyHeads}
                                    bodyHeadsTooltips={bodyHeadsTooltips}
                                    bodyColumns={bodyColumns}
                                    tickAltText={data.tables_tick_screen_reader_alt}
                                    disclaimer={globalStrings.financial_partner_disclaimer}
                                />
                            )
                        })}
                    </TablesWrapper>
                </BackgroundWrapper>
            )}

            {!!data.questions_answers.length && (
                <TitleWithAccordion
                    dataTestId={dataTestId}
                    title={data.questions_title}
                    items={data.questions_answers?.map(({question, answer}) => ({
                        title: question,
                        body: answer?.richText,
                    }))}
                />
            )}

            {data.testimonial.document && (
                <QuoteCarousel
                    dataTestId={`${dataTestId}-quotes`}
                    items={[
                        {
                            image: data.testimonial.document.data.image,
                            quote: data.testimonial.document.data.quote,
                            mirrored: true,
                            attribution: {
                                name: data.testimonial.document.data.attribution_name,
                                title: data.testimonial.document.data.attribution_title,
                            },
                        },
                    ]}
                />
            )}

            {data.social_proof_ratings_section.document && (
                <TitleWithIconGridSection
                    dataTestId={`${dataTestId}-ratings`}
                    titleVariant="heading-large"
                    highlightColor="pink"
                    title={data.social_proof_ratings_section.document.data.title.richText}
                    items={data.social_proof_ratings_section.document.data.ratings.map(
                        ({rating}) => ({
                            icon: rating.document?.data.logo,
                            stars: rating.document?.data.rating_number,
                            link: rating.document?.data.link,
                        }),
                    )}
                    {...(!hasStarsRating && {iconHeight: 187, iconWidth: 144})}
                />
            )}

            {data.social_proof_logos_extended_section.document && (
                <TitleWithIconGridSection
                    dataTestId={`${dataTestId}-logos`}
                    titleVariant="heading-large"
                    highlightColor="yellow"
                    title={data.social_proof_logos_extended_section.document.data.title.richText}
                    items={data.social_proof_logos_extended_section.document.data.logos.map(
                        ({logo}) => ({
                            icon: logo,
                        }),
                    )}
                />
            )}

            {data.why_pleo.map((block, i) => (
                <Section key={`${block.title}-${i}`} data-testid={`${dataTestId}-why-pleo`}>
                    <FeatureBlock
                        {...{...block, body: block.body.richText}}
                        preTitle={block.pre_title}
                    />
                </Section>
            ))}

            {data.signup_banner.document && (
                <SignupBannerSection
                    dataTestId={`${dataTestId}-signup-banner`}
                    {...data.signup_banner.document.data}
                    color="pink"
                    formConfig={
                        getEnvironmentFormConfig({
                            production: data.signup_banner.document.data.form_production,
                            staging: data.signup_banner.document.data.form_staging,
                        }) as FormConfig
                    }
                    placeholder={data.signup_banner.document.data.email_input_placeholder}
                    submitText={data.signup_banner.document.data.submit_text}
                    errorText={data.signup_banner.document.data.email_input_error_text}
                />
            )}
        </>
    )
}

const TablesWrapper = styled(Section)`
    display: grid;
    grid-gap: ${tokens.spacing12};

    ${mq.min('sm')} {
        grid-gap: ${tokens.spacing20};
    }
`

const StyledTable = styled(Table)`
    padding: ${tokens.spacing20} ${tokens.spacing16};
    background: ${colors.background.default};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
    }

    th:first-child {
        max-width: 120px;

        ${mq.min('sm')} {
            max-width: 240px;
        }

        ${mq.min('lg')} {
            max-width: 338px;
        }
    }
`

const TitleSection = styled(Section)`
    ${mq.min('sm')} {
        text-align: center;
    }

    ${mq.min('md')} {
        padding-top: 80px;
        padding-bottom: 80px;
    }

    strong {
        font-weight: ${fonts.weights.normal};
        background: ${colors.background.pink};
    }
`

const StyledImage = styled(Image)`
    margin-top: ${tokens.spacing10};
    margin-right: auto;
    margin-left: auto;
`

const TitleWrapper = styled(Layout)`
    margin-bottom: ${tokens.spacing8};

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing20};
    }
`
